import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActiveCartService, MultiCartService } from '@cart/data-access';
import { ConsignmentEntry, OrderEntry, PromotionLocation, UserIdService } from '@spartacus/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'sd-order-item-list',
  templateUrl: './order-item-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./order-item-list.component.scss'],
  standalone: false,
})
export class SdCustomOrderItemListComponent implements OnInit, OnDestroy {
  @Input() cartId: string;

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('items')
  set items(items: OrderEntry[]) {
    this.resolveItems(items);
  }

  get items(): OrderEntry[] {
    return this._items;
  }

  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  private subscription = new Subscription();
  private userId: string;
  private _items: OrderEntry[] = [];

  constructor(
    private activeCartService: ActiveCartService,
    private userIdService: UserIdService,
    private multiCartService: MultiCartService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(this.userIdService?.getUserId().subscribe((userId) => (this.userId = userId)));
  }

  removeEntry(item: OrderEntry): void {
    if (this.cartId && this.userId && item.positionGuid) {
      this.multiCartService?.removeEntry(this.userId, this.cartId, item.positionGuid);
    } else {
      this.activeCartService.removeEntry(item);
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * Resolves items passed to component input and updates 'items' field
   */
  private resolveItems(items: OrderEntry[]): void {
    if (!items) {
      this._items = [];

      return;
    }

    // The items we're getting from the input do not have a consistent model.
    // In case of a `consignmentEntry`, we need to normalize the data from the orderEntry.
    // eslint-disable-next-line no-prototype-builtins
    if (items.every((item) => item.hasOwnProperty('orderEntry'))) {
      this._items = items.map((consignmentEntry) => {
        const entry = Object.assign({}, (consignmentEntry as ConsignmentEntry).orderEntry);
        entry.quantity = consignmentEntry.quantity;

        return entry;
      });
    } else {
      // We'd like to avoid the unnecessary re-renders of unchanged cart items after the data reload.
      // OCC cart entries don't have any unique identifier that we could use in Angular `trackBy`.
      // So we update each array element to the new object only when it's any different to the previous one.
      for (let i = 0; i < Math.max(items.length, this._items.length); i++) {
        if (JSON.stringify(this._items?.[i]) !== JSON.stringify(items[i])) {
          if (!items[i]) {
            this._items.splice(i, 1);
          } else {
            this._items[i] = items[i];
          }
        }
      }
    }
  }
}
